import { generateUUID } from '@wix/form-schema';
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ComboBox as ComboBoxRAC,
  Input as InputRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
  TextField as TextFieldRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { type MlaAddressLineProps } from '../../../fields/mla-address-line/mla-address-line/mla-address-line-props';
import { useAddressAutofill } from '../../../fields/multiline-address/multiline-address/address-autofill-context';
import { useCountryCode } from '../../../fields/multiline-address/multiline-address/country-code-context';
import { useAddressLabel } from '../../../hooks/use-address-label';
import {
  applyInternalProps,
  findChildOfType,
} from '../../../services/find-child';
import { Description } from '../../field-description/field-description-headless';
import { Error } from '../../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../../field-label/field-label-headless';

import useAtlasHeadless from './use-atlas-headless';

export type AddressLineProps = {
  children: ReactNode;
};

export const AddressLine = ({ children }: AddressLineProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
    autocomplete,
  } = useFieldPropsV2<MlaAddressLineProps>();

  const { autocompleteEnabled } = useAddressAutofill();
  const isAutocomplete = autocomplete || autocompleteEnabled;

  const addressLineLabel = useAddressLabel('addressLine1');
  const labelElement = findChildOfType(children, AddressLine.Label);
  const inputElement = findChildOfType(children, AddressLine.Input);
  const optionsElement = findChildOfType(children, AddressLine.Options);
  const descriptionElement = findChildOfType(children, AddressLine.Description);
  const errorElement = findChildOfType(children, AddressLine.Error);

  if (isAutocomplete) {
    return (
      <FieldLayout
        fieldId={id}
        renderLabel={() =>
          applyInternalProps<LabelInternalProps>(labelElement, {
            label: addressLineLabel,
          })
        }
        renderInput={() => (
          <AddressLineAutocomplete
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            hasError={hasError}
            onBlur={onBlur}
            onFocus={onFocus}
          >
            {inputElement}
            {optionsElement}
          </AddressLineAutocomplete>
        )}
        renderDescription={() => (
          <>
            {descriptionElement}
            {errorElement}
          </>
        )}
      />
    );
  }

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, {
          label: addressLineLabel,
        })
      }
      renderInput={() => (
        <TextFieldRAC
          value={value ?? ''}
          onChange={onChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type AddressLineAutocompleteProps = {
  children: ReactNode;
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  hasError?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
};

const AddressLineAutocomplete = ({
  children,
  value,
  onChange,
  disabled,
  required,
  hasError,
  onBlur,
  onFocus,
}: AddressLineAutocompleteProps) => {
  const { onAddressSelect } = useAddressAutofill();
  const countryCode = useCountryCode();
  const [sessionToken, setSessionToken] = useState<string>('');

  const { predictions, updatePredictions, clearPredictions, getPlace } =
    useAtlasHeadless({ sessionToken });

  const options = useMemo(
    () =>
      predictions.map((prediction) => ({
        id: prediction.searchId || '',
        label: prediction.description || '',
      })),
    [predictions],
  );

  const handleInputChange = useCallback(
    (newValue: string) => {
      const countryCodes = countryCode ? [countryCode] : undefined;
      updatePredictions({ input: newValue, countryCodes });
      onChange(newValue);
    },
    [onChange, updatePredictions, countryCode],
  );

  const handleSelectionChange = useCallback(
    async (key: React.Key | null) => {
      if (!key) {
        return;
      }

      clearPredictions();

      const address = await getPlace(String(key));

      if (address && onAddressSelect) {
        onAddressSelect(address);
      }
    },
    [onAddressSelect, clearPredictions, getPlace],
  );

  const handleBlur = useCallback(() => {
    setSessionToken('');
    clearPredictions();
    onBlur?.();
  }, [onBlur, clearPredictions]);

  const handleFocus = useCallback(() => {
    setSessionToken(generateUUID());
    onFocus?.();
  }, [onFocus]);

  const inputElement = findChildOfType(children, AddressLine.Input);
  const optionsElement = findChildOfType(children, AddressLine.Options);

  return (
    <ComboBoxRAC
      inputValue={value ?? ''}
      onInputChange={handleInputChange}
      selectedKey={null}
      onSelectionChange={handleSelectionChange}
      isDisabled={disabled}
      isRequired={required}
      isInvalid={hasError}
      onBlur={handleBlur}
      onFocus={handleFocus}
      allowsCustomValue
      menuTrigger="input"
      items={options}
    >
      {applyInternalProps<InputInternalProps>(inputElement, {
        onFocus: handleFocus,
      })}
      {applyInternalProps<OptionsInternalProps>(optionsElement, {
        options,
      })}
    </ComboBoxRAC>
  );
};

type InputProps = {
  className?: string;
};

type InputInternalProps = {
  onFocus?: () => void;
};

const Input = ({ className, ...internal }: InputProps) => {
  const { id } = useFieldPropsV2<MlaAddressLineProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { onFocus } = internal as InputInternalProps;

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
      onFocus={onFocus}
    />
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

type OptionsInternalProps = {
  options: { id: string; label: string }[];
};

const Options = ({ children, className }: OptionsProps) => {
  const optionElement = findChildOfType(children, Options.Option);

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC renderEmptyState={() => null}>
        {(item: { id: string; label: string }) =>
          applyInternalProps<OptionInternalProps>(optionElement, {
            id: item.id,
            label: item.label,
          })
        }
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type OptionProps = {
  className?: string;
};

type OptionInternalProps = {
  id: string;
  label: string;
};

const Option = ({ className, ...internal }: OptionProps) => {
  const { id, label } = internal as OptionInternalProps;
  return (
    <ListBoxItemRAC className={className} id={id} textValue={label}>
      {label}
    </ListBoxItemRAC>
  );
};

Options.Option = Option;

AddressLine.Label = Label;
AddressLine.Input = Input;
AddressLine.Options = Options;
AddressLine.Description = Description;
AddressLine.Error = Error;
